import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, Download, Eye, FileText, Send, Wrench, X } from 'lucide-react';
import { ASSESSMENT_STATUSES, assessmentStatusLabels } from '../services/assessmentService';
import { reportService, REPORT_STATUSES, reportStatusLabels } from '../services/reportService';

const ReportPreviewModal = ({ report, onClose }) => (
  <div className="report-modal-overlay" role="dialog" aria-modal="true" aria-label="MindVrik report preview">
    <div className="report-modal-panel">
      <button className="platform-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
      <div className="section-label">Report Preview</div>
      <h2>{report.studentName}'s MindVrik Advisory Report</h2>
      <div className="report-section-grid">
        {report.sections.map(([title, text]) => (
          <div className="report-preview-section" key={title}>
            <FileText size={17} />
            <h3>{title}</h3>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ReportDashboardPage = ({ user, onBackToDashboard, onUserUpdate }) => {
  const [reportStatus, setReportStatus] = useState(REPORT_STATUSES.waitingForAssessment);
  const [report, setReport] = useState(null);
  const [message, setMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const assessmentStatus = user?.assessmentStatus || ASSESSMENT_STATUSES.notStarted;
  const reportReady = reportStatus === REPORT_STATUSES.reportReady || reportStatus === REPORT_STATUSES.sharedWithClient;

  useEffect(() => {
    const load = async () => {
      try {
        const status = await reportService.fetchReportStatus(user);
        const nextReport = await reportService.fetchReportByUser(user);
        setReportStatus(status);
        setReport(nextReport);
      } catch (error) {
        setReportStatus(REPORT_STATUSES.waitingForAssessment);
        setReport(null);
        setMessage(error.message || 'Report is not available yet.');
      }
    };
    load();
  }, [user]);

  const updateReportStatus = async (status) => {
    try {
      const updatedUser = await reportService.updateReportStatus(user, status);
      onUserUpdate?.(updatedUser);
      setReportStatus(status);
      const nextReport = await reportService.fetchReportByUser(updatedUser);
      setReport(nextReport);
      setMessage(`Status updated: ${reportStatusLabels[status]}`);
    } catch (error) {
      setMessage(error.message || 'Could not update report status.');
    }
  };

  const download = async () => {
    const result = await reportService.downloadReport(user);
    setMessage(result.message);
  };

  const share = async () => {
    const result = await reportService.shareReportWithClient(user);
    if (result.user) onUserUpdate?.(result.user);
    setMessage(result.message);
  };

  return (
    <section className="report-dashboard-page" aria-label="MindVrik report dashboard">
      <div className="report-dashboard-shell">
        <button className="checkout-back" onClick={onBackToDashboard}><ArrowLeft size={15} /> Back to Dashboard</button>
        <div className="report-dashboard-hero">
          <div>
            <div className="section-label">Report Dashboard</div>
            <h1>{user?.studentName || 'Student'}'s Report Status</h1>
            <p>Frontend-ready report architecture for MindVrik report tracking, counselor review, preview, download, and sharing.</p>
          </div>
          <div className="dashboard-user-pill"><FileText size={18} /><span>{reportStatusLabels[reportStatus]}</span></div>
        </div>

        <div className="report-status-grid">
          {[
            ['Student Name', user?.studentName || 'Not provided'],
            ['Selected Plan', user?.selectedPlan?.name || 'No plan selected'],
            ['Assessment Status', assessmentStatusLabels[assessmentStatus] || 'Not Started'],
            ['Report Status', reportStatusLabels[reportStatus]],
            ['Counselor Review Status', user?.counselorReviewStatus || report?.counselorReviewStatus || 'Pending'],
            ['Report Generated Date', user?.reportGeneratedDate ? new Date(user.reportGeneratedDate).toLocaleDateString() : 'Not generated yet'],
          ].map(([label, value]) => (
            <div className="report-status-card" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>

        <div className="report-action-row">
          <button className="btn-primary" onClick={() => setPreviewOpen(true)} disabled={!reportReady || !report}><Eye size={15} /> View Report</button>
          <button className="btn-ghost" onClick={download} disabled={!reportReady}><Download size={15} /> Download Report</button>
          <button className="btn-ghost" onClick={share} disabled={!reportReady}><Send size={15} /> Share with Parent/Client</button>
        </div>

        {message && <div className="checkout-message success">{message}</div>}

        <div className="dev-controls-card">
          <div>
            <Wrench size={18} />
            <strong>DEVELOPMENT ONLY</strong>
          <p>Temporary controls simulate partner callbacks until the real assessment platform is connected.</p>
          </div>
          <div className="dev-control-buttons">
            <button onClick={() => updateReportStatus(REPORT_STATUSES.assessmentSubmitted)}>Mark Assessment Submitted</button>
            <button onClick={() => updateReportStatus(REPORT_STATUSES.reportGenerating)}>Mark Report Generating</button>
            <button onClick={() => updateReportStatus(REPORT_STATUSES.reportReady)}>Mark Report Ready</button>
          </div>
        </div>

        <div className="platform-api-note">Reports are now stored in Supabase after assessment submission/status callback.</div>
      </div>
      {previewOpen && report && <ReportPreviewModal report={report} onClose={() => setPreviewOpen(false)} />}
    </section>
  );
};

export default ReportDashboardPage;
